import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Stack, Chip, InputAdornment, CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
  Store as StoreIcon,
  PhoneDisabled as PhoneDisabledIcon,
  SortByAlpha as SortByAlphaIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  fetchAuthorizedPayers,
  addAuthorizedPayer,
  updateAuthorizedPayer,
  deleteAuthorizedPayer,
} from '../slices/authorizedPayersSlice';
import { useToast } from '../context/ToastContext';

const MotionPaper = motion(Paper);
const FONT = 'Cairo, Tajawal, sans-serif';

const PHONE_CODE_OPTIONS = [
  { value: '972', label: '+972' },
  { value: '970', label: '+970' },
];

const emptyForm = { name: '', phone_number: '', phone_code: '970' };

const AuthorizedPayersPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { items: payers = [], loading } = useSelector((state) => state.authorizedPayers || {});

  const [search, setSearch] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('all');
  const [sortAZ, setSortAZ] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    dispatch(fetchAuthorizedPayers());
  }, [dispatch]);

  const filtered = payers
    .filter((p) => {
      const name = (p.name || p.display_value || '').toLowerCase();
      const phone = (p.phone_number || '').toLowerCase();
      const q = search.toLowerCase();
      if (q && !name.includes(q) && !phone.includes(q)) return false;
      if (phoneFilter === 'has') return !!p.phone_number;
      if (phoneFilter === 'no') return !p.phone_number;
      return true;
    })
    .sort((a, b) => {
      const nameA = (a.name || a.display_value || '');
      const nameB = (b.name || b.display_value || '');
      return sortAZ
        ? nameA.localeCompare(nameB, 'ar')
        : nameB.localeCompare(nameA, 'ar');
    });

  const openAdd = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (payer) => {
    setEditingId(payer.id);
    setFormData({
      name: payer.name || payer.display_value || '',
      phone_number: payer.phone_number || '',
      phone_code: payer.phone_code || '970',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;
    try {
      const payload = {
        name: formData.name.trim(),
        display_value: formData.name.trim(),
        phone_number: formData.phone_number?.trim() || null,
        phone_code: formData.phone_code || '970',
      };
      if (editingId) {
        await dispatch(updateAuthorizedPayer({ id: editingId, ...payload })).unwrap();
        showToast('✅ تم تحديث المورد بنجاح');
      } else {
        await dispatch(addAuthorizedPayer(payload)).unwrap();
        showToast('✅ تم إضافة المورد بنجاح');
      }
      setDialogOpen(false);
    } catch {
      showToast('❌ حدث خطأ، يرجى المحاولة مجدداً');
    }
  };

  const confirmDelete = (id) => {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteAuthorizedPayer(deletingId)).unwrap();
      showToast('✅ تم حذف المورد');
    } catch {
      showToast('❌ حدث خطأ أثناء الحذف');
    }
    setDeleteDialogOpen(false);
    setDeletingId(null);
  };

  const withPhone = payers.filter((p) => !!p.phone_number).length;
  const withoutPhone = payers.length - withPhone;

  return (
    <Box
      dir="rtl"
      sx={{
        fontFamily: FONT,
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>

        {/* Header */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StoreIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} color="#fff" sx={{ fontFamily: FONT }}>
                الموردون
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.25, fontFamily: FONT }}>
                إضافة وإدارة الموردين وربطهم بالمشتريات
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{
              bgcolor: '#fff',
              color: '#4338CA',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              boxShadow: 'none',
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: FONT,
              '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
            }}
          >
            إضافة مورد
          </Button>
        </MotionPaper>

        {/* Stats row */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.08 }}
          sx={{
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Stack useFlexGap
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            flexWrap="wrap"
          >
            {/* Search */}
            <TextField
              placeholder="بحث بالاسم أو رقم الهاتف..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{
                minWidth: 260,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  fontFamily: FONT,
                  bgcolor: '#F8F9FF',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#4338CA' },
                  '&.Mui-focused fieldset': { borderColor: '#4338CA' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: '#6B7280' }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Filter chips */}
            <Stack useFlexGap direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              {[
                { key: 'all', label: `الكل (${payers.length})` },
                { key: 'has', label: `لديه هاتف (${withPhone})` },
                { key: 'no', label: `بدون هاتف (${withoutPhone})` },
              ].map(({ key, label }) => (
                <Chip
                  key={key}
                  label={label}
                  size="small"
                  onClick={() => setPhoneFilter(key)}
                  icon={key === 'has' ? <PhoneIcon style={{ fontSize: 14 }} /> : key === 'no' ? <PhoneDisabledIcon style={{ fontSize: 14 }} /> : undefined}
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 700,
                    borderRadius: 2,
                    cursor: 'pointer',
                    bgcolor: phoneFilter === key ? '#4338CA' : '#EEF2FF',
                    color: phoneFilter === key ? '#fff' : '#4338CA',
                    border: phoneFilter === key ? 'none' : '1px solid #E5E7EB',
                    '& .MuiChip-icon': { color: phoneFilter === key ? '#fff' : '#4338CA' },
                    '&:hover': { bgcolor: phoneFilter === key ? '#3730A3' : '#E0E7FF' },
                  }}
                />
              ))}

              <Tooltip title={sortAZ ? 'ترتيب ي→أ' : 'ترتيب أ→ي'}>
                <IconButton
                  size="small"
                  onClick={() => setSortAZ((v) => !v)}
                  sx={{
                    color: '#4338CA',
                    bgcolor: '#EEF2FF',
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#E0E7FF' },
                  }}
                >
                  <SortByAlphaIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </MotionPaper>

        {/* Table */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15 }}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            mb: 3,
          }}
        >
          <TableContainer>
            <Table sx={{ direction: 'rtl' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                  <TableCell
                    sx={{
                      color: '#6B7280',
                      fontWeight: 700,
                      fontFamily: FONT,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderColor: '#E5E7EB',
                      width: 56,
                      textAlign: 'center',
                    }}
                  >
                    #
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#1E1B4B',
                      fontWeight: 700,
                      fontFamily: FONT,
                      borderColor: '#E5E7EB',
                      textAlign: 'right',
                    }}
                  >
                    اسم المورد
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#1E1B4B',
                      fontWeight: 700,
                      fontFamily: FONT,
                      borderColor: '#E5E7EB',
                      textAlign: 'right',
                    }}
                  >
                    رقم الهاتف
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#1E1B4B',
                      fontWeight: 700,
                      fontFamily: FONT,
                      borderColor: '#E5E7EB',
                      textAlign: 'center',
                    }}
                  >
                    الإجراءات
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6, borderColor: '#E5E7EB' }}>
                      <CircularProgress size={32} sx={{ color: '#4338CA' }} />
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 6, borderColor: '#E5E7EB' }}>
                      <Stack alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            bgcolor: '#EEF2FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <StoreIcon sx={{ color: '#4338CA', fontSize: 28 }} />
                        </Box>
                        <Typography sx={{ color: '#6B7280', fontFamily: FONT, fontWeight: 600 }}>
                          {search || phoneFilter !== 'all' ? 'لا توجد نتائج مطابقة للبحث' : 'لا يوجد موردون بعد'}
                        </Typography>
                        {(search || phoneFilter !== 'all') && (
                          <Button
                            size="small"
                            onClick={() => { setSearch(''); setPhoneFilter('all'); }}
                            sx={{ fontFamily: FONT, color: '#4338CA', textTransform: 'none', fontWeight: 700 }}
                          >
                            مسح الفلاتر
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((payer, idx) => {
                    const displayName = payer.name || payer.display_value || '';
                    return (
                      <TableRow
                        key={payer.id}
                        hover
                        sx={{
                          '&:hover': { bgcolor: '#F8F9FF' },
                          '& .MuiTableCell-root': { borderColor: '#E5E7EB' },
                          transition: 'background 0.15s',
                        }}
                      >
                        {/* Row number */}
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#9CA3AF', fontFamily: FONT, fontWeight: 700 }}>
                            {idx + 1}
                          </Typography>
                        </TableCell>

                        {/* Name */}
                        <TableCell sx={{ textAlign: 'right' }}>
                          <Typography
                            fontWeight={700}
                            sx={{ color: '#1E1B4B', fontFamily: FONT, fontSize: '0.9rem' }}
                          >
                            {displayName}
                          </Typography>
                        </TableCell>

                        {/* Phone */}
                        <TableCell sx={{ textAlign: 'right' }}>
                          {payer.phone_number ? (
                            <Chip
                              icon={<PhoneIcon style={{ fontSize: 14 }} />}
                              label={`+${payer.phone_code || '970'} ${payer.phone_number}`}
                              size="small"
                              sx={{
                                fontFamily: FONT,
                                fontWeight: 600,
                                bgcolor: '#EEF2FF',
                                color: '#4338CA',
                                border: '1px solid #C7D2FE',
                                direction: 'ltr',
                                '& .MuiChip-icon': { color: '#4338CA' },
                              }}
                            />
                          ) : (
                            <Chip
                              label="لا يوجد"
                              size="small"
                              sx={{
                                fontFamily: FONT,
                                fontWeight: 600,
                                bgcolor: '#F3F4F6',
                                color: '#9CA3AF',
                                border: '1px solid #E5E7EB',
                              }}
                            />
                          )}
                        </TableCell>

                        {/* Actions */}
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Stack useFlexGap direction="row" justifyContent="center" spacing={0.5}>
                            <Tooltip title="عرض التفاصيل">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/authorized-payers/${payer.id}`)}
                                sx={{
                                  color: '#4338CA',
                                  bgcolor: '#EEF2FF',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#C7D2FE' },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="تعديل">
                              <IconButton
                                size="small"
                                onClick={() => openEdit(payer)}
                                sx={{
                                  color: '#D97706',
                                  bgcolor: '#FEF3C7',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#FDE68A' },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="حذف">
                              <IconButton
                                size="small"
                                onClick={() => confirmDelete(payer.id)}
                                sx={{
                                  color: '#DC2626',
                                  bgcolor: '#FEE2E2',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#FECACA' },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Table footer */}
          {filtered.length > 0 && (
            <Box
              sx={{
                px: 3,
                py: 1.5,
                borderTop: '1px solid #E5E7EB',
                bgcolor: '#F8F9FF',
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT, fontWeight: 600 }}>
                عرض {filtered.length} من أصل {payers.length} مورد
              </Typography>
            </Box>
          )}
        </MotionPaper>

        {/* Add / Edit Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, direction: 'rtl', fontFamily: FONT } }}
        >
          <DialogTitle
            sx={{
              fontWeight: 800,
              color: '#4338CA',
              fontFamily: FONT,
              borderBottom: '1px solid #E5E7EB',
              pb: 2,
            }}
          >
            {editingId ? 'تعديل مورد' : 'إضافة مورد جديد'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2.5} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="اسم المورد"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                required
                inputProps={{ style: { textAlign: 'right', fontFamily: FONT } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    fontFamily: FONT,
                    '&:hover fieldset': { borderColor: '#4338CA' },
                    '&.Mui-focused fieldset': { borderColor: '#4338CA' },
                  },
                  '& .MuiInputLabel-root': { fontFamily: FONT },
                }}
              />
              {/* Phone: in RTL, code appears on the right, number on the left */}
              <Stack useFlexGap direction="row" spacing={0}>
                <TextField
                  label="رقم الهاتف (اختياري)"
                  value={formData.phone_number}
                  onChange={(e) => setFormData((p) => ({ ...p, phone_number: e.target.value }))}
                  inputProps={{ style: { direction: 'ltr', textAlign: 'left', fontFamily: FONT } }}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                      fontFamily: FONT,
                      '&:hover fieldset': { borderColor: '#4338CA' },
                      '&.Mui-focused fieldset': { borderColor: '#4338CA' },
                    },
                    '& .MuiInputLabel-root': { fontFamily: FONT },
                  }}
                />
                <FormControl sx={{ width: 110 }}>
                  <InputLabel sx={{ fontFamily: FONT }}>الرمز</InputLabel>
                  <Select
                    value={formData.phone_code}
                    onChange={(e) => setFormData((p) => ({ ...p, phone_code: e.target.value }))}
                    label="الرمز"
                    sx={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      fontFamily: FONT,
                      '& .MuiOutlinedInput-notchedOutline': { borderLeftWidth: 0 },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4338CA' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4338CA' },
                    }}
                  >
                    {PHONE_CODE_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value} sx={{ fontFamily: FONT, direction: 'ltr' }}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, borderTop: '1px solid #E5E7EB', mt: 1 }}>
            <Button
              onClick={() => setDialogOpen(false)}
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: FONT,
                borderColor: '#E5E7EB',
                color: '#6B7280',
                '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
              }}
            >
              إلغاء
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!formData.name.trim()}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: FONT,
                bgcolor: '#4338CA',
                boxShadow: 'none',
                px: 3,
                '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
              }}
            >
              {editingId ? 'حفظ التعديلات' : 'إضافة'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: 3, direction: 'rtl', fontFamily: FONT } }}
        >
          <DialogTitle sx={{ fontWeight: 800, color: '#DC2626', fontFamily: FONT }}>
            تأكيد الحذف
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#1E1B4B', fontFamily: FONT }}>
              هل أنت متأكد من حذف هذا المورد؟ لن يمكن التراجع عن هذا الإجراء.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: FONT,
                borderColor: '#E5E7EB',
                color: '#6B7280',
                '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
              }}
            >
              إلغاء
            </Button>
            <Button
              variant="contained"
              onClick={handleDelete}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: FONT,
                bgcolor: '#DC2626',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
              }}
            >
              حذف
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
};

export default AuthorizedPayersPage;
